import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowsClockwise, PaperPlaneTilt, Trash, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import GenderSplit from "@/components/admin/launch/GenderSplit";
import WaveEntries from "@/components/admin/launch/WaveEntries";
import { WAVE_TONE } from "@/components/admin/launch/WavesTab";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

function RuleWarning({ conflict, onForce, onCancel }) {
  return (
    <div className="cw-tone-warning flex flex-col gap-3 rounded-2xl p-4 text-sm" role="alert" data-testid="wave-gender-rule-warning">
      <p className="flex items-start gap-2 font-medium"><WarningCircle size={18} className="mt-0.5 shrink-0" />{conflict.message}</p>
      <GenderSplit items={conflict.split} testId="wave-conflict-split" />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onForce} data-testid="wave-force-button">Save anyway (logged as a warning)</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="wave-force-cancel-button">Cancel</Button>
      </div>
    </div>
  );
}

export default function WaveDetail({ id, onBack }) {
  const { data, error, loading, reload, setData } = useAdminLoad(`/admin/waves/${id}`);
  const [conflict, setConflict] = useState(null);
  const [ask, setAsk] = useState(null);
  const [sent, setSent] = useState(null);
  const w = data?.wave;
  const draft = w?.status === "draft";

  const change = async (body) => {
    try {
      setData(await api(`/admin/waves/${id}/entries`, { body }));
      setConflict(null);
      toast.success("Wave updated.");
    } catch (e) {
      if (e.code === "gender_rule") setConflict({ body, message: e.message, split: e.data?.gender_split || [] });
      else toast.error(e.message);
    }
  };
  const repick = async () => {
    try { setData(await api(`/admin/waves/${id}`, { method: "PATCH", body: { repick: true } })); toast.success("Picked again from the waitlist."); } catch (e) { toast.error(e.message); }
  };
  const send = async () => { const r = await api(`/admin/waves/${id}/send`, { method: "POST" }); setData({ wave: r.wave }); setSent(r); };
  const del = async () => { await api(`/admin/waves/${id}`, { method: "DELETE" }); toast.success("Draft wave deleted."); onBack(); };

  return (
    <div className="space-y-6" data-testid="wave-detail">
      <button onClick={onBack} className="cw-soft inline-flex items-center gap-2 rounded text-sm hover:text-foreground" data-testid="wave-back-button"><ArrowLeft size={16} />All waves</button>
      <LoadState loading={loading && !w} error={error} onRetry={reload}>
        {w && (<>
          <Panel className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2"><h2 className="text-xl font-semibold" data-testid="wave-detail-name">{w.name}</h2><Pill tone={WAVE_TONE[w.status]} className="capitalize" data-testid="wave-detail-status">{w.status}</Pill></div>
                <p className="cw-soft mt-1 text-sm">ZIPs {w.zips.join(", ")} · target {w.target_size} · max {w.max_gender_pct}% per gender</p>
                <p className="cw-muted mt-1 text-xs">Created {formatDate(w.created_at)} by {w.created_by}{w.sent_at ? ` · sent ${formatDate(w.sent_at)}` : ""}{w.opened_at ? ` · opened ${formatDate(w.opened_at)}` : ""}</p>
              </div>
              {draft && <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={repick} data-testid="wave-repick-button"><ArrowsClockwise />Auto-pick again</Button>
                <Button variant="ghost" className="cw-error" onClick={() => setAsk("delete")} data-testid="wave-delete-button"><Trash />Delete draft</Button>
                <Button onClick={() => setAsk("send")} disabled={!w.size} data-testid="wave-send-button"><PaperPlaneTilt />Send invites</Button>
              </div>}
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-[1fr_2fr]">
              <div className="text-sm tabular-nums" data-testid="wave-detail-counts"><p className="text-3xl font-semibold">{w.size}<span className="cw-muted text-base font-normal"> / {w.target_size}</span></p><p className="cw-soft">{w.invited} invited · {w.signed_up} signed up</p></div>
              <div><GenderSplit items={w.gender_split} max={w.max_gender_pct} testId="wave-detail-split" />{!w.rule_ok && <p className="cw-error mt-2 text-xs" data-testid="wave-rule-broken">One gender is over {w.max_gender_pct}%.</p>}</div>
            </div>
            {w.warnings?.length > 0 && <ul className="mt-5 space-y-1 text-xs" data-testid="wave-warnings">{w.warnings.map((x, i) => <li key={i} className="cw-tone-warning rounded-lg px-3 py-2">{x.message} · {x.by} · {formatDate(x.at)}</li>)}</ul>}
            {sent && <p className="cw-tone-success mt-5 rounded-xl px-4 py-3 text-sm" role="status" data-testid="wave-send-result">Invited {sent.invited}: {sent.emailed} emailed, {sent.test_logged} test addresses logged, {sent.failed} failed.</p>}
          </Panel>
          {conflict && <RuleWarning conflict={conflict} onCancel={() => setConflict(null)} onForce={() => change({ ...conflict.body, force: true })} />}
          <WaveEntries wave={w} onChange={change} />
        </>)}
      </LoadState>
      <ConfirmDialog open={ask === "send"} onOpenChange={(o) => !o && setAsk(null)} destructive={false} testId="wave-send-confirm-dialog" onConfirm={send}
        title={`Send ${w?.size} invites?`} confirmLabel="Send invites"
        description="Each person gets a single-use code by email (Content: wave_invite_email). Test addresses are logged, never emailed. You can't edit the wave afterwards." />
      <ConfirmDialog open={ask === "delete"} onOpenChange={(o) => !o && setAsk(null)} testId="wave-delete-confirm-dialog" onConfirm={del}
        title="Delete this draft wave?" confirmLabel="Delete wave" description="Everyone in it goes back to waiting on the waitlist." />
    </div>
  );
}
